/**
 * Role-scoped projections of the User entity, mirroring the per-resource
 * view pattern used by `Event*View` schemas. Views wrap their base shape in
 * `dataWithRolesSchema` so consumers can gate UI via
 * `data.$roles.includes('user:admin')`-style membership checks.
 */

import { z } from "zod";
import { UserSchema } from "./user.schemas.js";
import { UserRoleSchema } from "../constants/user-role.constants.js";
import { dataWithRolesSchema } from "./data-with-roles.schemas.js";

/** Fields the user is entitled to see about themselves (`GET /api/v1/users/me`). */
const _userOwnViewSchema = UserSchema.unwrap().pick({
  idPublic: true,
  identity: true,
  status: true,
  communicationChannels: true,
  preferredLocale: true,
  createdAt: true,
  updatedAt: true,
});

export const UserOwnViewSchema = dataWithRolesSchema(
  _userOwnViewSchema.readonly(),
  UserRoleSchema,
);
export type UserOwnView = z.infer<typeof UserOwnViewSchema>;

/** Fields an admin sees about a user they created (`POST /api/v1/admin/users`). */
const _userAdminViewSchema = UserSchema.unwrap().pick({
  identity: true,
  communicationChannels: true,
  roles: true,
  createdAt: true,
});

export const UserAdminViewSchema = dataWithRolesSchema(
  _userAdminViewSchema.readonly(),
  UserRoleSchema,
);
export type UserAdminView = z.infer<typeof UserAdminViewSchema>;
